import { API_V1, API_V2 } from '@front-app/services/api';
import { imageFetcher } from '@front-app/util/image';
import { parseFullEvent } from '../parse';
import {
	EventManagementData,
	EventV2,
	EventV2Result,
	FullEvent,
	FullEventResponse,
	RawPublicEventData,
	PublicEventData,
	EventNotificationType,
	EventNotification,
	UpsertEventNotification,
} from './EventModel';
import { parseEvents, parsePublicEventData } from './util';

export function getCurrentUserEvents(): Promise<EventV2[]> {
	return API_V2.get('/events')
		.then(({ data }) => data as EventV2Result[])
		.then(parseEvents);
}

export function getEventById(eventId: string): Promise<FullEvent> {
	return API_V1.get(`/event/${eventId}?trackUserView=false`)
		.then(({ data }) => data as FullEventResponse)
		.then(parseFullEvent);
}

export function getEventV2ById(eventId: string): Promise<EventV2> {
	return API_V1.get(`/event/${eventId}?trackUserView=false`)
		.then(({ data }) => data as FullEventResponse)
		.then(parseFullEvent)
		.then(fullEventToEventV2);
}

export function getPublicEventById(eventId: string): Promise<PublicEventData> {
	return API_V2.get(`/public/events/${eventId}`)
		.then(({ data }) => data as RawPublicEventData)
		.then(parsePublicEventData);
}

export const getEventLogo = imageFetcher((id: string) => `/events/${id}/logo`);
export const getEventBanner = imageFetcher((id: string) => `/events/${id}/banner`);
export const getEventVenue = imageFetcher((id: string) => `/events/${id}/floor-plan`);
export const getPublicEventBanner = imageFetcher((id: string) => `/public/events/${id}/banner`);

export function updateEventLogo(eventId: string, logo: File): Promise<void> {
	const innerRequest = new FormData();
	innerRequest.append('file', logo);
	return API_V2.post(`/event/${eventId}/logo`, innerRequest, {
		headers: {
			'Content-Type': 'multipart/form-data',
		},
	});
}

export function updateEventBanner(eventId: string, banner: File): Promise<void> {
	const innerRequest = new FormData();
	innerRequest.append('file', banner);
	return API_V2.post(`/event/${eventId}/banner`, innerRequest, {
		headers: {
			'Content-Type': 'multipart/form-data',
		},
	});
}

export function updateEventVenueFloorPlan(eventId: string, venueImage: File): Promise<void> {
	const innerRequest = new FormData();
	innerRequest.append('file', venueImage);
	return API_V2.post(`/event/${eventId}/floor-plan`, innerRequest, {
		headers: {
			'Content-Type': 'multipart/form-data',
		},
	});
}

export function getEventNotification(eventId: string, type: EventNotificationType): Promise<EventNotification> {
	return API_V2.get(`events/${eventId}/notifications/${type}`)
		.then(({ data }) => data as EventNotification);
};

export function createEventReminders(eventId: string): Promise<EventNotification> {
	return API_V2.post(`events/${eventId}/notifications/reminder`)
		.then(({ data }) => data as EventNotification);
}

export function updateEventNotification(
	eventId: string,
	type: EventNotificationType,
	updatedNotification: UpsertEventNotification
): Promise<EventNotification> {
	return API_V2.put(`events/${eventId}/notifications/${type}`, updatedNotification)
		.then(({ data }) => data as EventNotification);
}

function fullEventToEventV2(event: FullEvent): EventV2 {
	return {
		id: event.id,
		isPublished: event.isPublished,
		membershipRole: event.role,
		name: event.name,
		termsAndConditions: event.termsAndConditionsHtml,
		categories: event.categories,
		eventTypes: event.eventTypes,
		startDate: event.startDate,
		endDate: event.endDate,
		price: event.priceV2,
		location: event.address,
	};
}
export type UpsertEventRequest = Omit<EventManagementData, 'id' | 'groupIds'>;

/**
 * Only available to CRSs
 */
export function createEvent(payload: UpsertEventRequest): Promise<EventManagementData> {
	return API_V1.put('/event/admin', payload)
		.then(({ data }) => data as EventManagementData);
}

/**
 * Available to any event admin
 */
export function getAdminEvent(eventId: string): Promise<EventManagementData> {
	return API_V1.get(`/event/admin/${eventId}`)
		.then(({ data }) => data as EventManagementData);
}

/**
 * Available to any event admin
 */
export function updateEvent(eventId: string, payload: UpsertEventRequest): Promise<EventManagementData> {
	return API_V1.post(`/event/admin/${eventId}`, payload)
		.then(({ data }) => data as EventManagementData);
}
